/**
 * EditTermsDialog — terms, credit & categorization (Phase C).
 * Persists 7 fields:
 *
 *   1. Customer category — buyer segment label.
 *   2. Default revenue GL — pre-fill for the invoice header.
 *   3. Payment terms — Net-N picker (free-form on the wire).
 *   4. Credit limit (rupees → paise).
 *   5. Early-payment discount — free-text clause.
 *   6 + 7. Opening balance (rupees → paise) + as-of date, in a
 *          collapsible "Migration from Tally" block.
 *
 * `CreditVis` provides a visual headroom preview alongside the
 * limit input. Outstanding-balance integration lands later — for
 * now it shows "Brand new" / "Rs 0 used".
 *
 * @see prototype/sales/customer-new.html §6 — visual baseline
 * @see packages/db/migrations/0030_customer_phase_c.sql
 */
import * as React from "react";
import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
} from "@flint/ui";
import type { customers as contract } from "@flint/api-contract";
import type { z } from "zod";
import { EditDialog } from "./edit-dialog.tsx";
import { SectionField } from "./section-field.tsx";

type CustomerRow = z.infer<typeof contract.customerRow>;

const CUSTOMER_CATEGORIES: ReadonlyArray<{ value: string; label: string }> = [
  { value: "Retail", label: "Retail" },
  { value: "Wholesale / distributor", label: "Wholesale / distributor" },
  { value: "Manufacturer", label: "Manufacturer" },
  { value: "Government / PSU", label: "Government / PSU" },
  { value: "Export", label: "Export" },
  { value: "Service end-user", label: "Service end-user" },
];

const REVENUE_GLS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "4100 - Sales - fasteners", label: "4100 — Sales — fasteners" },
  { value: "4200 - Sales - services", label: "4200 — Sales — services" },
  { value: "4300 - Sales - export", label: "4300 — Sales — export" },
  { value: "4900 - Other revenue", label: "4900 — Other revenue" },
];

const PAYMENT_TERMS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "Net 0", label: "Due on receipt" },
  { value: "Net 7", label: "Net 7" },
  { value: "Net 15", label: "Net 15" },
  { value: "Net 30", label: "Net 30" },
  { value: "Net 45", label: "Net 45" },
  { value: "Net 60", label: "Net 60" },
  { value: "2/10 net 30", label: "2/10 net 30 (early-pay disc.)" },
];

export interface EditTermsDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  customer: CustomerRow;
  companyDefaultTerms?: string;
  pending: boolean;
  serverError: string | null;
  onSave: (patch: {
    customerCategory?: string;
    defaultRevenueGl?: string;
    paymentTerms?: string;
    creditLimitPaise?: string;
    earlyDiscount?: string;
    openingBalancePaise?: string;
    openingBalanceAsOf?: string;
  }) => Promise<void>;
}

export function EditTermsDialog({
  open,
  onOpenChange,
  customer,
  companyDefaultTerms = "Net 15",
  pending,
  serverError,
  onSave,
}: EditTermsDialogProps) {
  const [category, setCategory] = React.useState(customer.customerCategory ?? "");
  const [revenueGl, setRevenueGl] = React.useState(customer.defaultRevenueGl ?? "");
  const [terms, setTerms] = React.useState(customer.paymentTerms ?? "");
  const [creditLimit, setCreditLimit] = React.useState(
    paiseStringToRupeesString(customer.creditLimitPaise),
  );
  const [earlyDiscount, setEarlyDiscount] = React.useState(customer.earlyDiscount ?? "");
  const [openingBalance, setOpeningBalance] = React.useState(
    paiseStringToRupeesString(customer.openingBalancePaise),
  );
  const [asOf, setAsOf] = React.useState(customer.openingBalanceAsOf ?? "");

  React.useEffect(() => {
    if (open) {
      setCategory(customer.customerCategory ?? "");
      setRevenueGl(customer.defaultRevenueGl ?? "");
      setTerms(customer.paymentTerms ?? "");
      setCreditLimit(paiseStringToRupeesString(customer.creditLimitPaise));
      setEarlyDiscount(customer.earlyDiscount ?? "");
      setOpeningBalance(paiseStringToRupeesString(customer.openingBalancePaise));
      setAsOf(customer.openingBalanceAsOf ?? "");
    }
  }, [open, customer]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const patch: Parameters<EditTermsDialogProps["onSave"]>[0] = {};

    if (category.trim() !== (customer.customerCategory ?? "")) {
      patch.customerCategory = category.trim();
    }
    if (revenueGl.trim() !== (customer.defaultRevenueGl ?? "")) {
      patch.defaultRevenueGl = revenueGl.trim();
    }
    if (terms.trim() !== (customer.paymentTerms ?? "")) {
      patch.paymentTerms = terms.trim();
    }
    const nextLimitPaise = rupeesStringToPaiseString(creditLimit);
    if (nextLimitPaise !== (customer.creditLimitPaise ?? "")) {
      patch.creditLimitPaise = nextLimitPaise;
    }
    if (earlyDiscount.trim() !== (customer.earlyDiscount ?? "")) {
      patch.earlyDiscount = earlyDiscount.trim();
    }
    const nextOpeningPaise = rupeesStringToPaiseString(openingBalance);
    if (nextOpeningPaise !== (customer.openingBalancePaise ?? "")) {
      patch.openingBalancePaise = nextOpeningPaise;
    }
    if (asOf !== (customer.openingBalanceAsOf ?? "")) {
      patch.openingBalanceAsOf = asOf;
    }

    if (Object.keys(patch).length === 0) {
      onOpenChange(false);
      return;
    }
    await onSave(patch);
  };

  return (
    <EditDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Edit terms, credit & categorization"
      description="When they pay, how much you'll extend, and which book this customer sits in."
      pending={pending}
      serverError={serverError}
      onSubmit={handleSubmit}
      maxWidthClass="max-w-xl"
    >
      <div className="grid grid-cols-2 gap-3">
        <SectionField label="Customer category">
          <Select {...(category ? { value: category } : {})} onValueChange={setCategory}>
            <SelectTrigger>
              <SelectValue placeholder="Pick a segment…" />
            </SelectTrigger>
            <SelectContent>
              {CUSTOMER_CATEGORIES.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </SectionField>

        <SectionField label="Default revenue GL">
          <Select {...(revenueGl ? { value: revenueGl } : {})} onValueChange={setRevenueGl}>
            <SelectTrigger>
              <SelectValue placeholder="Pick a head…" />
            </SelectTrigger>
            <SelectContent>
              {REVENUE_GLS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </SectionField>
      </div>

      <SectionField
        label="Payment terms"
        hint={`Company default: ${companyDefaultTerms}. Sets the default due date on new invoices.`}
      >
        <Select {...(terms ? { value: terms } : {})} onValueChange={setTerms}>
          <SelectTrigger>
            <SelectValue placeholder={companyDefaultTerms} />
          </SelectTrigger>
          <SelectContent>
            {PAYMENT_TERMS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </SectionField>

      <div className="grid grid-cols-2 gap-3">
        <SectionField
          label="Credit limit (₹)"
          hint="Founder approval triggers if a single invoice would push past the limit."
        >
          <Input
            inputMode="numeric"
            value={creditLimit}
            onChange={(e) => setCreditLimit(e.target.value.replace(/[^\d,]/g, ""))}
            placeholder="5,00,000"
            className="font-mono tabular-nums"
          />
        </SectionField>

        <SectionField
          label="Early-payment discount"
          hint="Printed on the invoice — e.g. “2% if paid in 10 days”."
        >
          <Input
            value={earlyDiscount}
            onChange={(e) => setEarlyDiscount(e.target.value)}
            placeholder="e.g., 2% if paid in 10 days"
            maxLength={64}
          />
        </SectionField>
      </div>

      <CreditVis creditLimit={creditLimit} />

      <details className="rounded-md border border-line bg-bg/40 p-3 group">
        <summary className="text-sm font-semibold text-ink-2 cursor-pointer select-none">
          Migration from Tally — opening balance
        </summary>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <SectionField
            label="Opening balance (₹)"
            hint="From Tally / prior system. Leave blank for fresh customers."
          >
            <Input
              inputMode="numeric"
              value={openingBalance}
              onChange={(e) => setOpeningBalance(e.target.value.replace(/[^\d,]/g, ""))}
              placeholder="0"
              className="font-mono tabular-nums"
            />
          </SectionField>
          <SectionField label="As of">
            <Input
              type="date"
              value={asOf}
              onChange={(e) => setAsOf(e.target.value)}
              disabled={!openingBalance.trim()}
            />
          </SectionField>
        </div>
      </details>
    </EditDialog>
  );
}

/**
 * Headroom preview. Today: bar always shows 0% used because we don't
 * wire outstanding-balance into the dialog yet. When the receipts
 * vertical exposes a "currently-utilised" number on the customer row,
 * wire it in via a prop and recompute the bar.
 */
function CreditVis({ creditLimit }: { creditLimit: string }) {
  const limitDigits = creditLimit.replace(/[^\d]/g, "");
  const hasLimit = limitDigits.length > 0 && Number(limitDigits) > 0;
  const formatted = hasLimit ? formatRupees(limitDigits) : "No limit set";

  return (
    <div className={cn("rounded-md border border-line bg-paper p-3 flex flex-col gap-1.5")}>
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted">
          Credit headroom ·{" "}
          <strong className="text-ink-2">{hasLimit ? "Brand new" : "No limit"}</strong>
        </span>
        <span className="text-muted">
          <strong className="text-ink-2">₹0</strong> used /{" "}
          <strong className="text-ink-2">{formatted}</strong> available
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-bg overflow-hidden">
        <div className="h-full bg-sky-500" style={{ width: "0%" }} />
      </div>
      {hasLimit ? (
        <div className="text-xs text-muted leading-snug">
          No invoices yet — full{" "}
          <strong className="text-ink-2">{formatShortRupees(limitDigits)}</strong> headroom
          available for the first invoice.
        </div>
      ) : null}
    </div>
  );
}

function paiseStringToRupeesString(paise: string | null): string {
  if (!paise) return "";
  return (BigInt(paise) / 100n).toString();
}

function rupeesStringToPaiseString(rupees: string): string {
  const cleaned = rupees.replace(/[,\s]/g, "");
  if (!cleaned || !/^\d+$/.test(cleaned)) return "";
  return (BigInt(cleaned) * 100n).toString();
}

function formatRupees(digits: string): string {
  if (!digits) return "₹0";
  return `₹${new Intl.NumberFormat("en-IN").format(Number(digits))}`;
}

function formatShortRupees(digits: string): string {
  const n = Number(digits);
  if (!n) return "₹0";
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)}Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(2)}L`;
  return formatRupees(digits);
}
